import React, { useState, useRef, useCallback } from "react";

export default function AddressAutocomplete({ value, onChange, placeholder, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6&countrycodes=fr,be,ch,lu`,
          { headers: { "Accept-Language": "fr" } }
        );
        const data = await res.json();
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    onChange({ address: q, lat: null, lng: null, city: null, postcode: null });
    search(q);
  };

  const handleSelect = (item) => {
    const addr = item.address || {};
    const cityName =
      addr.city || addr.town || addr.village || addr.municipality || "";
    const road =
      addr.road || addr.pedestrian || addr.neighbourhood || "";

    // Nominatim géocode souvent au niveau rue (house_number absent).
    // Fallback : on récupère le n° tapé par l'utilisateur ("2B", "12 bis"...).
    const typedNumber = (value || "")
      .match(/^\s*(\d+[a-zA-Z]?(?:\s+(?:bis|ter|quater))?)\b/i)?.[1]
      ?.trim();
    const houseNumber = addr.house_number || typedNumber || "";

    // Adresse courte : "2B, Avenue de Versailles" (n° + rue), sinon 1er segment du display_name
    const shortAddress =
      [houseNumber, road].filter(Boolean).join(", ") ||
      item.display_name.split(",")[0].trim();

    // Ville avec code postal : "Paris 75017"
    const city = [cityName, addr.postcode].filter(Boolean).join(" ");

    onChange({
      address: shortAddress,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      city,
      postcode: addr.postcode || "",
    });
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value || ""}
        onChange={handleInput}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {loading && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/50 text-xs">
          ...
        </span>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto text-sm">
          {suggestions.map((s) => (
            <li
              key={s.place_id}
              className="px-3 py-2.5 hover:bg-blue-50 cursor-pointer text-base-content truncate border-b border-base-200 last:border-0 leading-tight"
              onMouseDown={() => handleSelect(s)}
            >
              <span className="font-medium text-base-content">
                {s.address?.road || s.address?.pedestrian || s.address?.neighbourhood || ""}
                {s.address?.house_number ? ` ${s.address.house_number}` : ""}
              </span>
              {(s.address?.city || s.address?.town || s.address?.village) && (
                <span className="text-base-content/60 ml-1">
                  — {s.address.city || s.address.town || s.address.village}
                  {s.address?.postcode ? ` (${s.address.postcode})` : ""}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
